import * as dat from 'dat.gui';
import { DebugMenuOptions, Folder, Prop, PropSliderWithButton, PropType } from './InitDebugMenu';
import { createDebugButton } from './Elements';

let gui: dat.GUI;

const defaultOptions: DebugMenuOptions = {
    useLocalStorage: false,
    showLanguage: true,
    showOnStart: false,
    props: [],
};

export function isFolder(prop: any): prop is Folder {
    return prop.folderName !== undefined;
}

function getPropItem(folder: dat.GUI, prop: Prop, settings: any, updateHandlers: any) {
    let guiEl: dat.GUIController;
    let parent: HTMLElement;
    let labelEl: HTMLElement;
    let btnEl: HTMLDivElement;
    switch (prop.type) {
        case PropType.BUTTON:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.onPress;
            return folder.add(settings, prop.propName);
        case PropType.LIST:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            return folder
                .add(settings, prop.propName, prop.items)
                .onChange(value => prop.onChange(value, prop.items.indexOf(value)));
        case PropType.NUMBER:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            return folder.add(settings, prop.propName).onFinishChange(prop.onFinishChange);
        case PropType.STRING:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            return folder.add(settings, prop.propName).onFinishChange(prop.onFinishChange);
        case PropType.TOGGLE:
            if (prop.hide) break;
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            return folder.add(settings, prop.propName).onChange(prop.onChange);
        case PropType.SLIDER:
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            return folder
                .add(settings, prop.propName, prop.min, prop.max, prop.step)
                .onFinishChange(prop.onFinishChange);
        case PropType.NUMBER_WITH_BUTTON:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            guiEl = folder.add(settings, prop.propName);

            parent = guiEl.domElement.parentElement;
            parent.style.display = 'flex';
            parent.style.flexDirection = 'row';
            parent.style.alignItems = 'center';

            labelEl = parent.getElementsByClassName('property-name').item(0) as HTMLElement;
            if (labelEl) labelEl.style.width = '55%';

            let value = 0;

            btnEl = createDebugButton(prop.buttonLabel, () => prop.onButtonClick(value));
            parent.appendChild(btnEl);

            return guiEl.onFinishChange(v => {
                value = v;
                prop.onFinishChange(value);
            });
        case PropType.LABEL:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            const element = folder.add(settings, prop.propName);
            createLabel(element);
            return element;
        case PropType.SLIDER_WITH_BUTTON:
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            const sliderElement = folder.add(settings, prop.propName, prop.min, prop.max, prop.step);
            createSliderWithButton(sliderElement, prop);
            return sliderElement;
        case PropType.STRING_WITH_BUTTON:
            // eslint-disable-next-line no-param-reassign
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            guiEl = folder.add(settings, prop.propName);

            parent = guiEl.domElement.parentElement;
            parent.style.display = 'flex';
            parent.style.flexDirection = 'row';
            parent.style.alignItems = 'center';

            labelEl = parent.getElementsByClassName('property-name').item(0) as HTMLElement;
            if (labelEl) labelEl.style.width = '34%';

            let strValue = '';

            btnEl = createDebugButton(prop.buttonLabel, () => prop.onButtonClick(strValue), { width: '14%' });
            parent.appendChild(btnEl);

            return guiEl.onFinishChange((v) => {
                strValue = v;
                prop.onFinishChange(strValue);
            });
        case PropType.LIST_WITH_BUTTON:
            // eslint-disable-next-line no-param-reassign
            let selectedValue = prop.initialValue;
            settings[prop.propName] = prop.initialValue;
            updateHandlers[prop.propName] = prop.onStateChanged;
            guiEl = folder.add(settings, prop.propName, prop.items).onChange(value => selectedValue = value);

            parent = guiEl.domElement.parentElement;
            parent.style.display = 'flex';
            parent.style.flexDirection = 'row';
            parent.style.alignItems = 'center';

            labelEl = parent.getElementsByClassName('property-name').item(0) as HTMLElement;
            if (labelEl) labelEl.style.width = '34%';

            btnEl = createDebugButton(prop.buttonLabel, () => prop.onButtonClick(selectedValue, prop.items.indexOf(selectedValue)), { width: '25%' });
            parent.appendChild(btnEl);

            return guiEl.onFinishChange((v) => {
                selectedValue = v;
                prop.onFinishChange(selectedValue, prop.items.indexOf(selectedValue));
            });
        default:
            break;
    }

    return undefined;
}

function addProps(
    folder: dat.GUI,
    settings: any,
    updateHandlers: any,
    props: (Prop | Folder)[],
    propList?: dat.GUIController[]
) {
    // eslint-disable-next-line no-param-reassign
    if (propList === undefined) propList = [];
    props?.forEach(prop => {
        if (isFolder(prop)) {
            const newFolder = folder.addFolder(prop.folderName);
            addProps(newFolder, settings, updateHandlers, prop.props, propList);
        } else {
            const item = getPropItem(folder, prop, settings, updateHandlers);

            if (item) {
                propList?.push(item);
            }
        }
    });

    return propList;
}

export default function createDebugMenu(o: DebugMenuOptions) {
    const options: DebugMenuOptions = {
        ...defaultOptions,
        ...o,
        props: [...defaultOptions.props, ...o.props],
    };

    gui = new dat.GUI({ width: options.width });

    gui.hide();

    gui.useLocalStorage = options.useLocalStorage!;

    const settings = {};
    const updateHandlers = {};

    const propList = addProps(gui, settings, updateHandlers, options.props!);

    createToggleButton();

    return {
        getProp: (propName: string) => propList.find(prop => prop.property === propName),
        getPropList: () => {
            return propList;
        },
        updateHandlers,
        gui,
    };
}

function createToggleButton(): void {
    const toggleButton = document.createElement('div');
    toggleButton.style.position = 'absolute';
    toggleButton.style.width = '40px';
    toggleButton.style.height = '40px';
    toggleButton.style.top = '0%';
    toggleButton.style.right = '0px';
    toggleButton.style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
    toggleButton.style.cursor = 'pointer';
    toggleButton.style.borderRadius = '50%';
    toggleButton.style.zIndex = '99999999';

    document.body.appendChild(toggleButton);

    toggleButton.addEventListener('click', () => {
        const hidden = gui.domElement.style.display === 'none';
        gui.domElement.style.display = hidden ? '' : 'none';
        if (hidden) gui.open();
    });
}

function createLabel(element: dat.GUIController): void {
    const parentEl = element.domElement.parentElement;
    parentEl.style.display = 'flex';
    parentEl.style.flexDirection = 'row';
    parentEl.style.alignItems = 'stretch';
    const propName = parentEl.getElementsByClassName('property-name').item(0) as HTMLElement;
    parentEl.removeChild(propName);
    const inputDiv = parentEl.getElementsByClassName('c')[0] as HTMLElement;
    if (inputDiv) {
        inputDiv.style.flexGrow = '1';
        const inputEl = inputDiv.firstChild as HTMLElement;
        inputEl.setAttribute('disabled', 'true');
        inputEl.style.color = 'gray';
        inputEl.style.backgroundColor = 'transparent';
    }
}

function createSliderWithButton(element: dat.GUIController, prop: PropSliderWithButton): void {
    const parent = element.domElement.parentElement;
    parent.style.display = 'flex';
    parent.style.flexDirection = 'row';
    parent.style.alignItems = 'center';

    const labelEl = parent.getElementsByClassName('property-name').item(0) as HTMLElement;
    if (labelEl) labelEl.style.width = '55%';

    let value = 0;

    const btnEl = createDebugButton(prop.buttonLabel, () => prop.onButtonClick(value));
    parent.appendChild(btnEl);

    element.onFinishChange(v => {
        value = v;
        prop.onFinishChange(value);
    });
}
